import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFirstLoginModal, setShowFirstLoginModal] = useState(false);
  const [pendingToken, setPendingToken] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (
          error.response?.data?.code === 'DOCUMENTS_ACCEPTANCE_REQUIRED' &&
          window.location.pathname !== '/acceptation-documents'
        ) {
          window.location.assign('/acceptation-documents');
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const fetchUserProfile = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/auth/me`);
      setUser(data);
      
      // Charger les permissions depuis le modèle Permission pour les admins
      if ((data.role === 'admin' || data.role === 'super_admin') && !data.documentsAAccepter?.length) {
        try {
          const permissionsRes = await axios.get(`${process.env.REACT_APP_API_URL}/permissions/me`);
          setPermissions(permissionsRes.data);
        } catch (permError) {
          console.error('Erreur lors du chargement des permissions:', permError);
          setPermissions(null);
        }
      }
    } catch (error) {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/auth/login`, {
      email,
      password,
    });
    
    // Vérifier si l'utilisateur doit compléter sa première connexion
    if (data.mustChangePassword || data.mustAcceptTerms) {
      // Stocker le token temporairement et afficher le modal
      setPendingToken(data.token);
      setUser(data);
      setShowFirstLoginModal(true);
      return { ...data, requiresFirstLogin: true };
    }
    
    localStorage.setItem('token', data.token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
    setUser(data);
    
    // Charger les permissions depuis le modèle Permission pour les admins
    if ((data.role === 'admin' || data.role === 'super_admin') && !data.documentsAAccepter?.length) {
      try {
        const permissionsRes = await axios.get(`${process.env.REACT_APP_API_URL}/permissions/me`);
        setPermissions(permissionsRes.data);
      } catch (permError) {
        console.error('Erreur lors du chargement des permissions:', permError);
        setPermissions(null);
      }
    }
    
    return data;
  };
  
  const completeFirstLogin = (updatedUser) => {
    // Après la complétion, sauvegarder le token et mettre à jour l'utilisateur
    if (pendingToken) {
      localStorage.setItem('token', pendingToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${pendingToken}`;
    }
    setUser({ ...user, ...updatedUser, mustChangePassword: false, mustAcceptTerms: false });
    setShowFirstLoginModal(false);
    setPendingToken(null);
  };

  const register = async (userData) => {
    const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/auth/register`, userData);
    localStorage.setItem('token', data.token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
    setUser(data);
    return data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setPermissions(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        permissions,
        loading,
        login,
        register,
        logout,
        refreshUser: fetchUserProfile,
        showFirstLoginModal,
        pendingToken,
        completeFirstLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
